import { useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router'
import { toast } from 'sonner'
import { useGetProduct } from '@/shared/hooks/api/product'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { MarkCompleteComplianceDialog } from '@/features/compliance-studio/machinery-directive/components/MarkCompleteComplianceDialog'
import { NotFoundView } from '@/features/errors/not-found-view'
import { CraStudioNav } from './CraStudioNav'
import { useGetCraStudio, useUpdateCraStudioCompleted } from './api'

function LayoutShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main fluid>{children}</Main>
    </>
  )
}

export function CraStudioPage() {
  const { t } = useTranslation('craCompliance')
  const { productId = '', craStudioId = '' } = useParams()
  const studioQuery = useGetCraStudio(craStudioId)
  const updateCompleted = useUpdateCraStudioCompleted(craStudioId)
  const [markCompleteDialogOpen, setMarkCompleteDialogOpen] = useState(false)
  const productQuery = useGetProduct(productId)
  const studioBaseHref = `/product-catalog/${productId}/cra/${craStudioId}`
  const backHref = `/v2/products/${productId}/cra`
  const directiveTitle =
    productQuery.data?.name?.trim() || studioQuery.data?.productName || ''

  if (studioQuery.isLoading) {
    return (
      <LayoutShell>
        <Skeleton className='h-64 w-full' />
      </LayoutShell>
    )
  }

  if (
    studioQuery.isError &&
    isAxiosError(studioQuery.error) &&
    studioQuery.error.response?.status === 404
  ) {
    return <NotFoundView />
  }

  const studio = studioQuery.data
  const isCompleted = studio?.completed === true
  const showMarkCompleteButton = studio != null && !isCompleted

  const handleMarkCompleteConfirm = async () => {
    try {
      const result = await updateCompleted.mutateAsync({ completed: true })
      const warnings = result.warnings ?? []
      for (const warning of warnings) {
        const key = `markComplete.warnings.${warning}`
        const message = t(key, { defaultValue: '' })
        if (message.length > 0) {
          toast.warning(message)
        }
      }
      toast.success(t('markComplete.dialog.successToast'))
      setMarkCompleteDialogOpen(false)
    } catch {
      toast.error(t('markComplete.dialog.failedToast'))
    }
  }

  return (
    <LayoutShell>
      <div className='flex flex-1 flex-col space-y-2 overflow-hidden md:space-y-2 lg:flex-row lg:space-y-0 lg:space-x-12'>
        <aside className='top-0 flex shrink-0 flex-col gap-2 lg:sticky lg:w-56'>
          <CraStudioNav
            studioBaseHref={studioBaseHref}
            backHref={backHref}
            productName={directiveTitle}
          />
          {showMarkCompleteButton && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='w-full justify-start'
              onClick={() => {
                setMarkCompleteDialogOpen(true)
              }}
            >
              <CircleCheck className='me-2 size-4 shrink-0' aria-hidden />
              {t('markComplete.button')}
            </Button>
          )}
          {isCompleted && (
            <Badge
              variant='default'
              className='w-full justify-center gap-1.5 bg-green-600 py-1.5 font-medium text-white'
            >
              <CheckCircle2 className='size-4' aria-hidden />
              {t('markComplete.completedLabel')}
            </Badge>
          )}
        </aside>
        <div className='flex w-full overflow-y-auto p-1'>
          <main className='w-full scrollbar-gutter-stable overflow-x-hidden 2xl:max-w-7xl'>
            <Outlet />
          </main>
        </div>
      </div>
      <MarkCompleteComplianceDialog
        isOpen={markCompleteDialogOpen}
        onClose={() => {
          setMarkCompleteDialogOpen(false)
        }}
        onConfirm={() => {
          void handleMarkCompleteConfirm()
        }}
        isSubmitting={updateCompleted.isPending}
        i18nNamespace='craCompliance'
      />
    </LayoutShell>
  )
}
